import { Injectable, signal } from '@angular/core';
import * as CANNON from 'cannon-es';
import {
  DiceRollerConfig,
  DiceRollResult,
  ThrowParameters,
  FaceDetectionResult,
  PhysicsFrame,
  CollisionEvent,
} from '../models/dice-roller.types';
import {
  DICE_FACE_ROTATIONS,
  DICE_FACE_ROTATIONS_VERTICAL,
  DEFAULT_PHYSICS_OPTIONS,
} from '../models/dice-faces.const';

/**
 * Physics scaling constants for consistent behavior across board sizes
 */
const PHYSICS_CONSTANTS = {
  /** Floor dimensions (large thin box) */
  FLOOR_SIZE: 50,
  FLOOR_THICKNESS: 0.1,
  
  /** Impulse application offset from center of mass (for spin effect) */
  IMPULSE_OFFSET_Z: 0.2,
  
  /** Perfect scaling exponent: S⁴ impulse scaling maintains constant angular velocity */
  IMPULSE_SCALING_EXPONENT: 4,
  
  /** Volume scaling exponent: S³ mass scaling maintains constant density */
  VOLUME_SCALING_EXPONENT: 3,
} as const;

/**
 * DicePhysicsService
 *
 * Implements record-and-replay physics for dice rolling with 100% accuracy.
 *
 * **Architecture:**
 * - **Simulation World**: Runs physics once to find target value
 * - **Frame Recording**: Captures every frame's position/quaternion
 * - **Playback**: Component replays recorded frames (no physics divergence)
 *
 * **Key Features:**
 * - 100% visual accuracy: Replay eliminates dual-world divergence
 * - Predetermined mode: Pre-calculates throw to guarantee specific outcome
 * - Random mode: Generates random throw without pre-calculation
 * - Scene rotation awareness: Adapts face detection for flat/vertical AR scenes
 *
 * **Face Detection:**
 * Uses face normal vectors to determine which face points upward (+Z direction).
 * Robust for all orientations and scene rotations.
 */
@Injectable({
  providedIn: 'root',
})
export class DicePhysicsService {
  private simulationWorld: CANNON.World | null = null;

  /**
   * Signal tracking current roll state for UI feedback
   */
  readonly rollState = signal<'idle' | 'calculating' | 'rolling'>('idle');

  constructor() {}

  /**
   * Resets physics worlds and state. Call when game restarts.
   */
  reset(): void {
    // Clear simulation world
    if (this.simulationWorld) {
      this.simulationWorld.bodies.forEach(body => {
        this.simulationWorld?.removeBody(body);
      });
      this.simulationWorld = null;
    }
    
    this.rollState.set('idle');
  }

  /**
   * Initializes the simulation world (hidden, for pre-calculation)
   */
  private initSimulationWorld(config: DiceRollerConfig): CANNON.World {
    const world = new CANNON.World({
      gravity: new CANNON.Vec3(
        ...(config.gravity || DEFAULT_PHYSICS_OPTIONS.gravity)
      ),
    });
    
    // CRITICAL: Use NaiveBroadphase for reliable collision detection
    // Default SAPBroadphase can miss Plane-Box collisions
    world.broadphase = new CANNON.NaiveBroadphase();
    
    // Enable sleep detection for proper simulation
    world.allowSleep = true;

    world.defaultContactMaterial.restitution =
      DEFAULT_PHYSICS_OPTIONS.restitution;
    world.defaultContactMaterial.friction = DEFAULT_PHYSICS_OPTIONS.friction;
    
    // CRITICAL: Explicitly create contact material for floor-die collisions
    const floorMaterial = new CANNON.Material('floor');
    const dieMaterial = new CANNON.Material('die');
    const floorDieContact = new CANNON.ContactMaterial(floorMaterial, dieMaterial, {
      friction: DEFAULT_PHYSICS_OPTIONS.friction,
      restitution: DEFAULT_PHYSICS_OPTIONS.restitution,
    });
    world.addContactMaterial(floorDieContact);
    
    // Store materials for later use
    (world as any).floorMaterial = floorMaterial;
    (world as any).dieMaterial = dieMaterial;

    // Add floor at configured position
    // CRITICAL: Use Box instead of Plane for reliable collision detection
    // Cannon.js Plane-Box collision is notoriously unreliable
    const floorPos = config.floorPosition || [0, 0, PHYSICS_CONSTANTS.FLOOR_THICKNESS];
    const floorBody = new CANNON.Body({
      type: CANNON.Body.STATIC,
      shape: new CANNON.Box(
        new CANNON.Vec3(
          PHYSICS_CONSTANTS.FLOOR_SIZE,
          PHYSICS_CONSTANTS.FLOOR_SIZE,
          PHYSICS_CONSTANTS.FLOOR_THICKNESS
        )
      ),
      position: new CANNON.Vec3(floorPos[0], floorPos[1], floorPos[2]),
      material: (world as any).floorMaterial // CRITICAL: Apply collision material
    });
    
    // Mark floor body for collision detection
    (floorBody as any).isFloor = true;
    
    // Cannon.js Plane default orientation: Normal points along +Z
    // This is CORRECT for Z-down gravity - blocks objects falling from above
    // No rotation needed!
    
    world.addBody(floorBody);
    
    return world;
  }

  /**
   * Finds valid throw parameters that will result in the target value.
   *
   * **Algorithm:**
   * 1. Generate random throw parameters (seed-based)
   * 2. Create simulation die in physics world
   * 3. Run physics until die sleeps
   * 4. Detect landed face
   * 5. Record frames for playback
   * 6. If matches target, return parameters; else retry
   *
   * **Max Attempts:** Configurable (default 100, typically finds valid throw in 10-50 attempts)
   *
   * @param targetValue Desired face value (1-6)
   * @param config Dice configuration including scene rotation
   * @returns Promise resolving to validated throw parameters
   * @throws Error if valid throw not found after max attempts
   */
  async findValidThrow(
    targetValue: number,
    config: DiceRollerConfig
  ): Promise<ThrowParameters> {
    this.rollState.set('calculating');

    // Initialize simulation world if needed
    if (!this.simulationWorld) {
      this.simulationWorld = this.initSimulationWorld(config);
    }

    const maxAttempts = config.maxAttempts || DEFAULT_PHYSICS_OPTIONS.maxAttempts || 100;
    let attemptCount = 0;
    
    // Add random offset to seed to avoid identical throws for same target
    const seedOffset = Math.floor(Math.random() * 10000);

    while (attemptCount < maxAttempts) {
      attemptCount++;

      // Generate random throw parameters with randomized seed
      const params = this.generateRandomThrow(seedOffset + attemptCount, config);

      // Create simulation die matching visual die size (size * scale)
      const baseSize = config.size || 0.6;
      const scaleFactor = config.scale || 1.0;
      const dieSize = baseSize * scaleFactor;
      
      // Mass should scale with volume (S³) to maintain constant density
      // This ensures physics feels consistent across all board sizes
      const mass = scaleFactor ** PHYSICS_CONSTANTS.VOLUME_SCALING_EXPONENT;
      
      const dieBody = new CANNON.Body({
        mass: mass,
        shape: new CANNON.Box(
          new CANNON.Vec3(dieSize / 2, dieSize / 2, dieSize / 2)
        ),
        position: new CANNON.Vec3(...params.startPos),
        quaternion: new CANNON.Quaternion(
          params.startRotation.x,
          params.startRotation.y,
          params.startRotation.z,
          params.startRotation.w
        ),
        sleepTimeLimit: DEFAULT_PHYSICS_OPTIONS.sleepTimeLimit,
        sleepSpeedLimit: DEFAULT_PHYSICS_OPTIONS.sleepSpeedLimit,
        material: (this.simulationWorld as any).dieMaterial // CRITICAL: Apply collision material
      });

      this.simulationWorld.addBody(dieBody);
      
      // Apply impulse with offset for spin
      // Perfect scaling: Force scales by S⁴ to maintain consistent angular velocity
      // Linear velocity scales proportionally: ΔV = (F·S⁴)/(S³) = F·S
      // This gives larger dice proportionally faster motion to match visual scale
      const scaledForce = params.force * (scaleFactor ** PHYSICS_CONSTANTS.IMPULSE_SCALING_EXPONENT);
      dieBody.applyImpulse(
        new CANNON.Vec3(
          params.offset[0] * scaledForce,
          params.offset[1] * scaledForce,
          params.offset[2] * scaledForce
        ),
        new CANNON.Vec3(0, 0, PHYSICS_CONSTANTS.IMPULSE_OFFSET_Z) // Constant offset maintains spin consistency
      );

      // Create array to record simulation frames for playback
      const recordedFrames: PhysicsFrame[] = [];

      // Simulate until sleep
      const landedValue = await this.simulateUntilSleep(
        dieBody,
        config.sceneRotation || [0, 0, 0],
        recordedFrames
      );

      // Remove die from simulation world
      this.simulationWorld.removeBody(dieBody);
      
      // CRITICAL: Reset world time to prevent accumulation across attempts
      // This ensures simulation world matches render world's initial state
      this.simulationWorld.time = 0;

      // Check if landed value matches target
      if (landedValue === targetValue) {
        this.rollState.set('idle');
        return { ...params, recordedFrames };
      }
      
      // After many attempts, accept any stable result to avoid infinite loops
      if (attemptCount >= maxAttempts * 0.8) {
        this.rollState.set('idle');
        return { ...params, recordedFrames };
      }
    }

    this.rollState.set('idle');
    throw new Error(
      `Failed to find valid throw for value ${targetValue} after ${maxAttempts} attempts`
    );
  }

  /**
   * Generates random throw parameters with seed-based determinism.
   *
   * **Parameters:**
   * - startPos: Starting position above floor
   * - startRotation: Random initial rotation as quaternion
   * - force: Random force magnitude (3-8 range)
   * - offset: Direction vector for impulse
   *
   * @param seed Random seed for reproducibility
   * @param config Dice configuration including start position
   * @returns Throw parameters
   */
  private generateRandomThrow(seed: number, config?: DiceRollerConfig): ThrowParameters {
    // Seed-based random (simple linear congruential generator)
    const random = (s: number) => {
      const x = Math.sin(s) * 10000;
      return x - Math.floor(x);
    };

    // Use configured start position or generate random position
    let startPos: [number, number, number];
    if (config?.startPosition) {
      // Use configured position with small random offset
      const [x, y, z] = config.startPosition;
      startPos = [
        x + random(seed * 1.1) * 1 - 0.5, // ±0.5 offset
        y + random(seed * 1.2) * 1 - 0.5,
        z + random(seed * 1.3) * 0.5, // Smaller Z offset
      ];
    } else {
      // Random position above floor (top surface at Z = FLOOR_THICKNESS * 2)
      startPos = [
        random(seed * 1.1) * 2 - 1, // X: -1 to 1
        random(seed * 1.2) * 2 - 1, // Y: -1 to 1
        1.5 + random(seed * 1.3) * 1, // Z: 1.5 to 2.5 (well above floor)
      ];
    }

    // Random rotation as Euler angles, convert to quaternion
    const eulerX = random(seed * 2.1) * 2 * Math.PI;
    const eulerY = random(seed * 2.2) * 2 * Math.PI;
    const eulerZ = random(seed * 2.3) * 2 * Math.PI;

    const quat = new CANNON.Quaternion();
    quat.setFromEuler(eulerX, eulerY, eulerZ, 'XYZ');

    const startRotation = {
      x: quat.x,
      y: quat.y,
      z: quat.z,
      w: quat.w,
    };

    const force = 3 + random(seed * 3.1) * 5; // 3-8

    const offset: [number, number, number] = [
      -1, // Left direction
      1, // Up direction
      random(seed * 3.2) * 0.5 - 0.25, // Z: -0.25 to 0.25
    ];

    return {
      startPos,
      startRotation,
      force,
      offset,
      recordedFrames: [], // Will be populated during simulation
    };
  }

  /**
   * Simulates physics until die sleeps (stops moving).
   *
   * **Algorithm:**
   * - Steps physics world with fixed time step
   * - Checks velocity against sleep threshold
   * - Detects stable state (velocity < sleepSpeedLimit for > sleepTimeLimit)
   * - Returns landed face value
   * - Records every frame for later playback
   * - Records collision events for audio playback
   *
   * **Max Simulation Time:** 5 seconds (safety limit)
   *
   * @param body Die physics body
   * @param sceneRotation Scene rotation for face detection
   * @param recordedFrames Array to store recorded physics frames for playback
   * @returns Promise resolving to landed face value (1-6)
   */
  private async simulateUntilSleep(
    body: CANNON.Body,
    sceneRotation: [number, number, number],
    recordedFrames: PhysicsFrame[]
  ): Promise<number> {
    const maxTime = (DEFAULT_PHYSICS_OPTIONS.timeout || 3000) / 1000; // Convert ms to seconds
    const timeStep = 1 / 60; // 60 FPS
    let elapsed = 0;
    const sleepSpeedLimit = DEFAULT_PHYSICS_OPTIONS.sleepSpeedLimit;
    const sleepTimeLimit = DEFAULT_PHYSICS_OPTIONS.sleepTimeLimit;
    const stableFramesRequired = Math.ceil(sleepTimeLimit / timeStep); // 0.02s / (1/60s) = 2 frames
    
    let frameCount = 0;
    let stableFrames = 0;
    
    // Track collision in current frame for recording
    let currentFrameCollision: CollisionEvent | undefined = undefined;
    
    // Set up collision event listener for die-to-surface and die-to-die collisions
    const collisionHandler = (event: any) => {
      // Calculate impact velocity magnitude
      const impactVelocity = event.contact?.getImpactVelocityAlongNormal() || 0;
      const intensity = Math.min(Math.abs(impactVelocity) / 30, 1); // Normalize to 0-1 range (30 = max expected velocity)
      
      // Filter out insignificant impacts (threshold: 0.1)
      if (intensity < 0.1) return;
      
      // Determine collision type by checking if other body is the floor
      const otherBody = event.body === body ? event.target : event.body;
      const isFloorCollision = (otherBody as any).isFloor === true;
      
      // Keep only the strongest collision per frame to avoid audio spam
      if (!currentFrameCollision || intensity > currentFrameCollision.intensity) {
        currentFrameCollision = {
          type: isFloorCollision ? 'die-to-surface' : 'die-to-die',
          intensity
        };
      }
    };
    
    body.addEventListener('collide', collisionHandler);

    while (elapsed < maxTime) {
      // Reset collision tracking for this frame
      currentFrameCollision = undefined;
      
      // CRITICAL: Use step() not fixedStep() - fixedStep(dt) doesn't integrate properly
      this.simulationWorld!.step(timeStep);
      elapsed += timeStep;
      frameCount++;
      
      // Record this frame for playback (guarantees 100% accuracy)
      recordedFrames.push({
        position: [body.position.x, body.position.y, body.position.z],
        quaternion: [body.quaternion.x, body.quaternion.y, body.quaternion.z, body.quaternion.w],
        collision: currentFrameCollision
      });

      // Check if die is moving slowly enough
      const speed = body.velocity.length();
      const angularSpeed = body.angularVelocity.length();

      if (speed < sleepSpeedLimit && angularSpeed < sleepSpeedLimit) {
        stableFrames++;

        // If stable for enough frames, check if face is clearly upward (not on edge)
        if (stableFrames >= stableFramesRequired) {
          const result = this.detectLandedFace(body, sceneRotation);
          
          // Only accept if die is stable (not on edge)
          if (result.isStable) {
            // Clean up event listener
            body.removeEventListener('collide', collisionHandler);
            return result.value;
          }
          // If on edge, reset and keep simulating
          stableFrames = 0;
        }
      } else {
        stableFrames = 0; // Reset frame count if die starts moving again
      }
    }

    // Timeout - return detected face even if not fully stable
    console.warn('⏱️ [SIMULATION] Timeout after', frameCount, 'frames');
    
    // Clean up event listener
    body.removeEventListener('collide', collisionHandler);
    
    const result = this.detectLandedFace(body, sceneRotation);
    return result.value;
  }

  /**
   * Detects which face of the die is facing up based on rotation.
   *
   * **Algorithm:**
   * For Z-up gravity ([0,0,-30]), determines which face normal points in +Z direction.
   * Maps dice faces to their local normals, transforms by quaternion, and finds which
   * points most upward. This is robust and works regardless of scene rotation.
   *
   * **Standard Die Face Mapping:**
   * - Face 1: +Y local (top when die at rest)
   * - Face 6: -Y local (bottom)
   * - Face 2: +X local (right)
   * - Face 5: -X local (left)
   * - Face 3: +Z local (front)
   * - Face 4: -Z local (back)
   *
   * @param body Die physics body
   * @param sceneRotation Scene rotation context (for future AR mode support)
   * @returns Face detection result with value, stability, and rotation
   */
  detectLandedFace(
    body: CANNON.Body,
    sceneRotation: [number, number, number]
  ): FaceDetectionResult {
    // Vector-based detection for Z-up coordinate system
    const faceNormals: { value: number; normal: CANNON.Vec3 }[] = [
      { value: 1, normal: new CANNON.Vec3(0, 1, 0) },  // +Y → face 1
      { value: 6, normal: new CANNON.Vec3(0, -1, 0) }, // -Y → face 6
      { value: 2, normal: new CANNON.Vec3(1, 0, 0) },  // +X → face 2
      { value: 5, normal: new CANNON.Vec3(-1, 0, 0) }, // -X → face 5
      { value: 3, normal: new CANNON.Vec3(0, 0, 1) },  // +Z → face 3
      { value: 4, normal: new CANNON.Vec3(0, 0, -1) }, // -Z → face 4
    ];

    // Up vector in Z-up coordinate system
    const upVector = new CANNON.Vec3(0, 0, 1);

    let maxDot = -Infinity;
    let faceValue = 1;

    for (const face of faceNormals) {
      const worldNormal = body.quaternion.vmult(face.normal);
      const dot = worldNormal.dot(upVector);
      
      if (dot > maxDot) {
        maxDot = dot;
        faceValue = face.value;
      }
    }

    // Stricter stability check - ensure face is clearly upward
    const isStable = maxDot > 0.85; // Increased from 0.7 for stricter edge detection

    const euler = new CANNON.Vec3();
    body.quaternion.toEuler(euler);

    return {
      value: faceValue,
      isStable,
      rotation: [euler.x, euler.y, euler.z],
    };
  }

  /**
   * Checks if scene is rotated to vertical AR mode.
   *
   * **Vertical Mode Detection:**
   * Scene rotated approximately -90° on X-axis ([-π/2, 0, 0])
   *
   * @param sceneRotation Scene rotation as Euler angles
   * @returns True if vertical mode
   */
  private isVerticalMode(sceneRotation: [number, number, number]): boolean {
    const eps = 0.1;
    return Math.abs(sceneRotation[0] + Math.PI / 2) < eps;
  }

  /**
   * Executes validated throw in render world for visible animation.
   *
   * **Usage:**
   * 1. Pre-calculate throw with findValidThrow()
   * 2. Pass parameters to this method
   * 3. Apply to visible dice body in render world
   * 4. Animate using render loop
   *
   * @param params Pre-calculated throw parameters
   * @param body Visible dice body in render world
   */
  /**
   * Cleanup simulation world resources.
   */
  destroy(): void {
    if (this.simulationWorld) {
      this.simulationWorld.bodies.forEach(body => {
        this.simulationWorld?.removeBody(body);
      });
      this.simulationWorld = null;
    }
    this.rollState.set('idle');
  }
}
